import React, { FC, useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { useSubscriptionStore, BillingType, BillingInfo } from 'src/store/subscriptionStore';
import {
  useBillingProfileQuery,
  useSaveBillingProfileMutation,
} from 'src/api/subscription/useSubscriptionApi';
import { toast } from 'src/utils/appAlert';
import { ReactComponent as IndividualSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-information/individual.svg';
import { ReactComponent as BuildingSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/common/building.svg';
import './SubscriptionModals.scss';

interface BillingInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const WORLD_COUNTRIES = [
  'Afghanistan',
  'Albania',
  'Algeria',
  'Andorra',
  'Angola',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bhutan',
  'Bolivia',
  'Bosnia and Herzegovina',
  'Botswana',
  'Brazil',
  'Brunei',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cabo Verde',
  'Cambodia',
  'Cameroon',
  'Canada',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Colombia',
  'Comoros',
  'Congo (Congo-Brazzaville)',
  'Costa Rica',
  'Croatia',
  'Cuba',
  'Cyprus',
  'Czechia (Czech Republic)',
  'Democratic Republic of the Congo',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Eswatini',
  'Ethiopia',
  'Fiji',
  'Finland',
  'France',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Greece',
  'Grenada',
  'Guatemala',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Holy See',
  'Honduras',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran',
  'Iraq',
  'Ireland',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  'Kuwait',
  'Kyrgyzstan',
  'Laos',
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Mauritania',
  'Mauritius',
  'Mexico',
  'Micronesia',
  'Moldova',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Morocco',
  'Mozambique',
  'Myanmar (Burma)',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'North Korea',
  'North Macedonia',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestine State',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Poland',
  'Portugal',
  'Qatar',
  'Romania',
  'Russia',
  'Rwanda',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Korea',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Sweden',
  'Switzerland',
  'Syria',
  'Taiwan',
  'Tajikistan',
  'Tanzania',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Venezuela',
  'Vietnam',
  'Yemen',
  'Zambia',
  'Zimbabwe',
];

const findMatchingCountry = (countryName: string): string | undefined => {
  if (!countryName) return undefined;
  const normalized = countryName.trim().toLowerCase();

  // Exact match
  const exact = WORLD_COUNTRIES.find((c) => c.toLowerCase() === normalized);
  if (exact) return exact;

  // Partial / alias match
  return WORLD_COUNTRIES.find(
    (c) => c.toLowerCase().includes(normalized) || normalized.includes(c.toLowerCase()),
  );
};

export const detectBrowserCountry = (): string => {
  try {
    // 1. Gather all potential locale strings from the browser
    const candidateLocales: string[] = [];

    // From resolved Intl options
    const resolvedLocale = Intl.DateTimeFormat().resolvedOptions().locale;
    if (resolvedLocale) candidateLocales.push(resolvedLocale);

    // From navigator.languages / navigator.language
    if (typeof navigator !== 'undefined') {
      if (Array.isArray(navigator.languages)) {
        candidateLocales.push(...navigator.languages);
      }
      if (navigator.language) {
        candidateLocales.push(navigator.language);
      }
    }

    // 2. Iterate through candidate locales to extract standard ISO region code
    for (const localeStr of candidateLocales) {
      if (!localeStr) continue;

      let regionCode: string | undefined;

      // Use Intl.Locale if available
      if (typeof Intl.Locale === 'function') {
        try {
          const localeObj = new Intl.Locale(localeStr);
          regionCode = localeObj.region;
          if (!regionCode && typeof localeObj.maximize === 'function') {
            regionCode = localeObj.maximize().region;
          }
        } catch {
          // Ignore invalid locale string parse errors
        }
      }

      // Regex fallback if Intl.Locale did not yield region
      if (!regionCode) {
        const match = localeStr.match(/[-_]([A-Za-z]{2})\b/);
        if (match) {
          regionCode = match[1].toUpperCase();
        }
      }

      if (regionCode && regionCode.length === 2) {
        if (typeof Intl.DisplayNames === 'function') {
          const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
          const englishName = regionNames.of(regionCode);
          if (englishName) {
            const matched = findMatchingCountry(englishName);
            if (matched) return matched;
          }
        }
      }
    }

    // 3. TimeZone-based dynamic resolution
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timeZone) {
      for (const c of WORLD_COUNTRIES) {
        const cleanCountry = c.toLowerCase().replace(/[^a-z]/g, '');
        const cleanTz = timeZone.toLowerCase().replace(/[^a-z]/g, '');
        if (cleanCountry.length > 3 && cleanTz.includes(cleanCountry)) {
          return c;
        }
      }
    }
  } catch {
    // Fallback safely
  }

  return 'India';
};

interface SearchableCountrySelectProps {
  value: string;
  onChange: (val: string) => void;
  onBlur: () => void;
  hasError?: boolean;
}

const SearchableCountrySelect: FC<SearchableCountrySelectProps> = ({
  value,
  onChange,
  onBlur,
  hasError,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const filteredCountries = WORLD_COUNTRIES.filter((c) =>
    c.toLowerCase().includes(searchQuery.toLowerCase().trim()),
  );

  const handleSelect = (c: string) => {
    onChange(c);
    setIsOpen(false);
    onBlur();
  };

  return (
    <div className={`custom-country-select-wrapper ${isOpen ? 'is-open' : ''}`} ref={dropdownRef}>
      <button
        type="button"
        id="billing-country-trigger"
        className={`custom-country-trigger ${hasError ? 'has-error' : ''} ${!value ? 'is-placeholder' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="selected-country-label">{value || 'Choose from list'}</span>
        <svg
          className={`dropdown-chevron ${isOpen ? 'open' : ''}`}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M6 9L12 15L18 9"
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="custom-country-dropdown-menu">
          <div className="search-input-box">
            <svg
              className="search-icon"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="rgba(255,255,255,0.4)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {searchQuery && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
              >
                {'×'}
              </button>
            )}
          </div>
          <div className="country-list-scroll">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((c) => (
                <div
                  key={c}
                  className={`country-item ${c === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(c)}
                >
                  <span>{c}</span>
                  {c === value && (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ffa100"
                      strokeWidth="2.5"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              ))
            ) : (
              <div className="no-countries-found">{'No country found'}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface IndividualFormData {
  fullName: string;
  billingEmail: string;
  country: string;
  stateRegion: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  taxVatNumber: string;
}

interface BusinessFormData {
  companyName: string;
  billingEmail: string;
  country: string;
  stateRegion: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  companyRegistrationNumber: string;
  taxVatNumber: string;
}

export const BillingInfoModal: FC<BillingInfoModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { billingInfo, updateBillingInfo } = useSubscriptionStore();
  const saveProfileMutation = useSaveBillingProfileMutation();
  const { data: serverProfile } = useBillingProfileQuery(isOpen);

  const [billingType, setBillingType] = useState<BillingType>(
    serverProfile?.billingType || billingInfo.billingType || 'individual',
  );

  const [individualData, setIndividualData] = useState<IndividualFormData>({
    fullName: '',
    billingEmail: '',
    country: detectBrowserCountry(),
    stateRegion: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    taxVatNumber: '',
  });

  const [businessData, setBusinessData] = useState<BusinessFormData>({
    companyName: '',
    billingEmail: '',
    country: detectBrowserCountry(),
    stateRegion: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    companyRegistrationNumber: '',
    taxVatNumber: '',
  });

  const [isDefault, setIsDefault] = useState(billingInfo.isDefault ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isOpen) {
      const source: BillingInfo =
        serverProfile &&
        (serverProfile.fullName || serverProfile.companyName || serverProfile.addressLine1)
          ? {
              billingType: serverProfile.billingType || billingInfo.billingType,
              fullName: serverProfile.fullName || billingInfo.fullName,
              companyName: serverProfile.companyName || billingInfo.companyName,
              billingEmail: serverProfile.billingEmail || billingInfo.billingEmail,
              country: serverProfile.country || billingInfo.country,
              stateRegion: serverProfile.stateRegion || billingInfo.stateRegion,
              addressLine1: serverProfile.addressLine1 || billingInfo.addressLine1,
              addressLine2: serverProfile.addressLine2 || billingInfo.addressLine2,
              city: serverProfile.city || billingInfo.city,
              postcode: serverProfile.postcode || billingInfo.postcode,
              companyRegistrationNumber:
                serverProfile.companyRegistrationNumber || billingInfo.companyRegistrationNumber,
              taxVatNumber: serverProfile.taxVatNumber || billingInfo.taxVatNumber,
              isDefault: serverProfile.isDefault ?? billingInfo.isDefault,
            }
          : billingInfo;

      const activeType = source.billingType || 'individual';
      const isSaved = Boolean(source.fullName || source.companyName || source.addressLine1);
      const chosenCountry = isSaved && source.country ? source.country : detectBrowserCountry();

      setBillingType(activeType);

      if (activeType === 'individual') {
        setIndividualData({
          fullName: source.fullName || '',
          billingEmail: source.billingEmail || '',
          country: chosenCountry,
          stateRegion: source.stateRegion || '',
          addressLine1: source.addressLine1 || '',
          addressLine2: source.addressLine2 || '',
          city: source.city || '',
          postcode: source.postcode || '',
          taxVatNumber: source.taxVatNumber || '',
        });
        setBusinessData({
          companyName: '',
          billingEmail: '',
          country: detectBrowserCountry(),
          stateRegion: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          postcode: '',
          companyRegistrationNumber: '',
          taxVatNumber: '',
        });
      } else {
        setBusinessData({
          companyName: source.companyName || '',
          billingEmail: source.billingEmail || '',
          country: chosenCountry,
          stateRegion: source.stateRegion || '',
          addressLine1: source.addressLine1 || '',
          addressLine2: source.addressLine2 || '',
          city: source.city || '',
          postcode: source.postcode || '',
          companyRegistrationNumber: source.companyRegistrationNumber || '',
          taxVatNumber: source.taxVatNumber || '',
        });
        setIndividualData({
          fullName: '',
          billingEmail: '',
          country: detectBrowserCountry(),
          stateRegion: '',
          addressLine1: '',
          addressLine2: '',
          city: '',
          postcode: '',
          taxVatNumber: '',
        });
      }

      setIsDefault(source.isDefault ?? true);
      setErrors({});
      setTouched({});
    }
  }, [isOpen, billingInfo, serverProfile]);

  const handleSwitchBillingType = (type: BillingType) => {
    if (type === billingType) return;
    setBillingType(type);
    setErrors({});
    setTouched({});
  };

  const handleBlur = (field: string, value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, value.trim());
  };

  const validateField = (field: string, value: string) => {
    let error = '';
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (field === 'fullName' && billingType === 'individual') {
      if (!value.trim()) error = 'Full Name is required';
      else if (!/\p{L}/u.test(value)) error = 'Full Name must contain letters';
    } else if (field === 'companyName' && billingType === 'business') {
      if (!value.trim()) error = 'Company Name is required';
      else if (!/\p{L}/u.test(value)) error = 'Company Name must contain letters';
    } else if (field === 'billingEmail') {
      if (!value.trim()) error = 'Billing Email is required';
      else if (!emailRegex.test(value.trim())) error = 'Please enter a valid email address';
    } else if (field === 'country') {
      if (!value.trim()) error = 'Country is required';
      else if (!WORLD_COUNTRIES.includes(value.trim())) error = 'Please select a valid country';
    } else if (field === 'addressLine1') {
      if (!value.trim()) error = 'Address Line 1 is required';
    } else if (field === 'city') {
      if (!value.trim()) error = 'City is required';
      else if (!/\p{L}/u.test(value)) error = 'City must contain letters';
    } else if (field === 'postcode') {
      if (!value.trim()) error = 'Postcode is required';
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleSave = () => {
    const isInd = billingType === 'individual';
    const newErrors: Record<string, string> = {};
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    if (isInd) {
      if (!individualData.fullName.trim()) newErrors.fullName = 'Full Name is required';
      else if (!/\p{L}/u.test(individualData.fullName))
        newErrors.fullName = 'Full Name must contain letters';

      if (!individualData.billingEmail.trim()) newErrors.billingEmail = 'Billing Email is required';
      else if (!emailRegex.test(individualData.billingEmail.trim()))
        newErrors.billingEmail = 'Please enter a valid email address';

      if (!individualData.country.trim()) newErrors.country = 'Country is required';
      else if (!WORLD_COUNTRIES.includes(individualData.country.trim()))
        newErrors.country = 'Please select a valid country';

      if (!individualData.addressLine1.trim())
        newErrors.addressLine1 = 'Address Line 1 is required';
      if (!individualData.city.trim()) newErrors.city = 'City is required';
      else if (!/\p{L}/u.test(individualData.city)) newErrors.city = 'City must contain letters';

      if (!individualData.postcode.trim()) newErrors.postcode = 'Postcode is required';

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setTouched({
          fullName: true,
          billingEmail: true,
          country: true,
          addressLine1: true,
          city: true,
          postcode: true,
        });
        return;
      }

      const updated: Partial<BillingInfo> = {
        billingType: 'individual',
        fullName: individualData.fullName.trim(),
        companyName: '',
        billingEmail: individualData.billingEmail.trim(),
        country: individualData.country.trim(),
        stateRegion: individualData.stateRegion.trim(),
        addressLine1: individualData.addressLine1.trim(),
        addressLine2: individualData.addressLine2.trim(),
        city: individualData.city.trim(),
        postcode: individualData.postcode.trim(),
        companyRegistrationNumber: '',
        taxVatNumber: individualData.taxVatNumber.trim(),
        isDefault,
      };

      saveProfileMutation.mutate(updated, {
        onSuccess: () => {
          updateBillingInfo(updated);
          toast.success('Billing information saved.');
          if (onSuccess) onSuccess();
          onClose();
        },
        onError: (err: any) => {
          if (err.fieldErrors) {
            setErrors(err.fieldErrors);
          }
          toast.error(err.userMessage || 'Failed to save billing information.');
        },
      });
    } else {
      if (!businessData.companyName.trim()) newErrors.companyName = 'Company Name is required';
      else if (!/\p{L}/u.test(businessData.companyName))
        newErrors.companyName = 'Company Name must contain letters';

      if (!businessData.billingEmail.trim()) newErrors.billingEmail = 'Billing Email is required';
      else if (!emailRegex.test(businessData.billingEmail.trim()))
        newErrors.billingEmail = 'Please enter a valid email address';

      if (!businessData.country.trim()) newErrors.country = 'Country is required';
      else if (!WORLD_COUNTRIES.includes(businessData.country.trim()))
        newErrors.country = 'Please select a valid country';

      if (!businessData.addressLine1.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
      if (!businessData.city.trim()) newErrors.city = 'City is required';
      else if (!/\p{L}/u.test(businessData.city)) newErrors.city = 'City must contain letters';

      if (!businessData.postcode.trim()) newErrors.postcode = 'Postcode is required';

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setTouched({
          companyName: true,
          billingEmail: true,
          country: true,
          addressLine1: true,
          city: true,
          postcode: true,
        });
        return;
      }

      const updated: Partial<BillingInfo> = {
        billingType: 'business',
        fullName: '',
        companyName: businessData.companyName.trim(),
        billingEmail: businessData.billingEmail.trim(),
        country: businessData.country.trim(),
        stateRegion: businessData.stateRegion.trim(),
        addressLine1: businessData.addressLine1.trim(),
        addressLine2: businessData.addressLine2.trim(),
        city: businessData.city.trim(),
        postcode: businessData.postcode.trim(),
        companyRegistrationNumber: businessData.companyRegistrationNumber.trim(),
        taxVatNumber: businessData.taxVatNumber.trim(),
        isDefault,
      };

      saveProfileMutation.mutate(updated, {
        onSuccess: () => {
          updateBillingInfo(updated);
          toast.success('Billing information saved.');
          if (onSuccess) onSuccess();
          onClose();
        },
        onError: (err: any) => {
          if (err.fieldErrors) {
            setErrors(err.fieldErrors);
          }
          toast.error(err.userMessage || 'Failed to save billing information.');
        },
      });
    }
  };

  const isIndividual = billingType === 'individual';

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={640}
      closeIcon={null}
      rootClassName="subscription-modal-wrapper"
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(6, 7, 35, 0.75)',
        },
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
        noValidate
      >
        <div className="modal-header">
          <h2>{'Billing Information'}</h2>
          <p>{'These information will be used for invoices and receipts'}</p>
        </div>

        {/* Segmented Billing Type */}
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label>{'Billing type'}</label>
          <div className="billing-type-tabs">
            <button
              type="button"
              className={`type-tab-btn ${isIndividual ? 'active' : ''}`}
              onClick={() => handleSwitchBillingType('individual')}
            >
              <IndividualSVG />
              <span>{'Individual'}</span>
            </button>
            <button
              type="button"
              className={`type-tab-btn ${!isIndividual ? 'active' : ''}`}
              onClick={() => handleSwitchBillingType('business')}
            >
              <BuildingSVG />
              <span>{'Business'}</span>
            </button>
          </div>
        </div>

        {/* Row 1: Name & Email */}
        <div className="form-row stack-on-mobile">
          {isIndividual ? (
            <div className="form-group">
              <label htmlFor="billing-full-name">{'FULL NAME*'}</label>
              <input
                id="billing-full-name"
                type="text"
                value={individualData.fullName}
                maxLength={70}
                className={touched.fullName && errors.fullName ? 'has-error' : ''}
                onChange={(e) =>
                  setIndividualData((prev) => ({ ...prev, fullName: e.target.value }))
                }
                onBlur={(e) => {
                  setIndividualData((prev) => ({ ...prev, fullName: e.target.value.trim() }));
                  handleBlur('fullName', e.target.value);
                }}
              />
              {touched.fullName && errors.fullName && (
                <span className="error-text">{errors.fullName}</span>
              )}
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="billing-company-name">{'COMPANY NAME*'}</label>
              <input
                id="billing-company-name"
                type="text"
                value={businessData.companyName}
                maxLength={100}
                className={touched.companyName && errors.companyName ? 'has-error' : ''}
                onChange={(e) =>
                  setBusinessData((prev) => ({ ...prev, companyName: e.target.value }))
                }
                onBlur={(e) => {
                  setBusinessData((prev) => ({ ...prev, companyName: e.target.value.trim() }));
                  handleBlur('companyName', e.target.value);
                }}
              />
              {touched.companyName && errors.companyName && (
                <span className="error-text">{errors.companyName}</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="billing-email">{'BILLING EMAIL*'}</label>
            <input
              id="billing-email"
              type="email"
              value={isIndividual ? individualData.billingEmail : businessData.billingEmail}
              maxLength={255}
              className={touched.billingEmail && errors.billingEmail ? 'has-error' : ''}
              onChange={(e) => {
                const val = e.target.value;
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, billingEmail: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, billingEmail: val }));
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, billingEmail: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, billingEmail: val }));
                }
                handleBlur('billingEmail', e.target.value);
              }}
            />
            {touched.billingEmail && errors.billingEmail && (
              <span className="error-text">{errors.billingEmail}</span>
            )}
          </div>
        </div>

        {/* Row 2: Country & State */}
        <div className="form-row stack-on-mobile">
          <div className="form-group">
            <label htmlFor="billing-country-trigger">{'COUNTRY*'}</label>
            <SearchableCountrySelect
              value={isIndividual ? individualData.country : businessData.country}
              onChange={(val) => {
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, country: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, country: val }));
                }
                validateField('country', val);
              }}
              onBlur={() =>
                handleBlur('country', isIndividual ? individualData.country : businessData.country)
              }
              hasError={touched.country && !!errors.country}
            />
            {touched.country && errors.country && (
              <span className="error-text">{errors.country}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="billing-state">{'STATE/REGION (OPTIONAL)'}</label>
            <input
              id="billing-state"
              type="text"
              value={isIndividual ? individualData.stateRegion : businessData.stateRegion}
              maxLength={60}
              onChange={(e) => {
                const val = e.target.value;
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, stateRegion: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, stateRegion: val }));
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, stateRegion: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, stateRegion: val }));
                }
              }}
            />
          </div>
        </div>

        {/* Row 3: Address Line 1 & Line 2 */}
        <div className="form-row stack-on-mobile">
          <div className="form-group">
            <label htmlFor="billing-address-1">{'ADDRESS LINE 1*'}</label>
            <input
              id="billing-address-1"
              type="text"
              value={isIndividual ? individualData.addressLine1 : businessData.addressLine1}
              maxLength={120}
              className={touched.addressLine1 && errors.addressLine1 ? 'has-error' : ''}
              onChange={(e) => {
                const val = e.target.value;
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, addressLine1: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, addressLine1: val }));
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, addressLine1: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, addressLine1: val }));
                }
                handleBlur('addressLine1', e.target.value);
              }}
            />
            {touched.addressLine1 && errors.addressLine1 && (
              <span className="error-text">{errors.addressLine1}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="billing-address-2">{'ADDRESS LINE 2 (OPTIONAL)'}</label>
            <input
              id="billing-address-2"
              type="text"
              value={isIndividual ? individualData.addressLine2 : businessData.addressLine2}
              maxLength={120}
              onChange={(e) => {
                const val = e.target.value;
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, addressLine2: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, addressLine2: val }));
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, addressLine2: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, addressLine2: val }));
                }
              }}
            />
          </div>
        </div>

        {/* Row 4: City & Postcode */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="billing-city">{'CITY*'}</label>
            <input
              id="billing-city"
              type="text"
              value={isIndividual ? individualData.city : businessData.city}
              maxLength={60}
              className={touched.city && errors.city ? 'has-error' : ''}
              onChange={(e) => {
                const val = e.target.value;
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, city: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, city: val }));
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, city: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, city: val }));
                }
                handleBlur('city', e.target.value);
              }}
            />
            {touched.city && errors.city && <span className="error-text">{errors.city}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="billing-postcode">{'POSTCODE*'}</label>
            <input
              id="billing-postcode"
              type="text"
              value={isIndividual ? individualData.postcode : businessData.postcode}
              maxLength={20}
              className={touched.postcode && errors.postcode ? 'has-error' : ''}
              onChange={(e) => {
                const val = e.target.value;
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, postcode: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, postcode: val }));
                }
              }}
              onBlur={(e) => {
                const val = e.target.value.trim();
                if (isIndividual) {
                  setIndividualData((prev) => ({ ...prev, postcode: val }));
                } else {
                  setBusinessData((prev) => ({ ...prev, postcode: val }));
                }
                handleBlur('postcode', e.target.value);
              }}
            />
            {touched.postcode && errors.postcode && (
              <span className="error-text">{errors.postcode}</span>
            )}
          </div>
        </div>

        {/* Business extra fields or optional VAT */}
        {!isIndividual ? (
          <div className="form-row stack-on-mobile">
            <div className="form-group">
              <label htmlFor="billing-company-reg">
                {'COMPANY REGISTRATION NUMBER (OPTIONAL)'}
              </label>
              <input
                id="billing-company-reg"
                type="text"
                value={businessData.companyRegistrationNumber}
                maxLength={30}
                onChange={(e) =>
                  setBusinessData((prev) => ({
                    ...prev,
                    companyRegistrationNumber: e.target.value,
                  }))
                }
                onBlur={(e) =>
                  setBusinessData((prev) => ({
                    ...prev,
                    companyRegistrationNumber: e.target.value.trim(),
                  }))
                }
              />
            </div>

            <div className="form-group">
              <label htmlFor="billing-tax-vat">{'TAX / VAT NUMBER (OPTIONAL)'}</label>
              <input
                id="billing-tax-vat"
                type="text"
                value={businessData.taxVatNumber}
                maxLength={30}
                onChange={(e) =>
                  setBusinessData((prev) => ({ ...prev, taxVatNumber: e.target.value }))
                }
                onBlur={(e) =>
                  setBusinessData((prev) => ({ ...prev, taxVatNumber: e.target.value.trim() }))
                }
              />
            </div>
          </div>
        ) : (
          <div className="form-row single">
            <div className="form-group">
              <label htmlFor="billing-tax-vat">{'TAX / VAT NUMBER (OPTIONAL)'}</label>
              <input
                id="billing-tax-vat"
                type="text"
                value={individualData.taxVatNumber}
                maxLength={30}
                onChange={(e) =>
                  setIndividualData((prev) => ({ ...prev, taxVatNumber: e.target.value }))
                }
                onBlur={(e) =>
                  setIndividualData((prev) => ({ ...prev, taxVatNumber: e.target.value.trim() }))
                }
              />
            </div>
          </div>
        )}

        {/* Checkbox Default */}
        <label className="checkbox-group">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span>{'Set as default billing information'}</span>
        </label>

        {/* Actions */}
        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            {'Cancel'}
          </button>
          <button type="submit" className="btn-save" disabled={saveProfileMutation.isPending}>
            {saveProfileMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
